import React, { useMemo } from 'react'
import { DateField } from '@/components/molecules/date-field'
import { css } from '@emotion/core'
import dayjs, { Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'
import { mainContainerStyle } from '@/constants/layout'
import { StaffType } from '@/constants/cleaning-manager'

const weekdays = ['日', '月', '火', '水', '木', '金', '土']

export type ScheduleType = {
  date: string
  totalRoomCleaned: number
  totalRoomNotClean: number
  totalRoomNotCleanWithStay: number
  totalRoomNotCleanWithOut: number
  totalRoomInspected: number
  totalRoomDoNotDisturb: number
}

interface ScheduleListProps {
  dateOf: Dayjs
  setDateOf: React.Dispatch<React.SetStateAction<Dayjs>>
  monthSummary: ScheduleType[]
  staffList: StaffType[]
  createDates: string[]
  onShiftChange: ({ staffId, dateOf }: { staffId: string; dateOf: string }) => void
}

export const ScheduleList: React.FC<ScheduleListProps> = ({ setDateOf, createDates, dateOf, monthSummary, staffList, onShiftChange }) => {
  const { t } = useTranslation()

  const fontColorOfDay = (day: number) => {
    if (day === 0) {
      return '#F47110'
    } else if (day === 6) {
      return '#3E85CC'
    } else {
      return '#A3A3A3'
    }
  }

  const computedTotal = useMemo(() => {
    return monthSummary.reduce(
      (prev, current) => ({
        notCleanWithStayMonth: prev.notCleanWithStayMonth + current.totalRoomNotCleanWithStay,
        notCleanWithOutMonth: prev.notCleanWithOutMonth + current.totalRoomNotCleanWithOut,
        cleanedMonth: prev.cleanedMonth + current.totalRoomCleaned,
        inspectedMonth: prev.inspectedMonth + current.totalRoomInspected,
        doNotDisturbMonth: prev.doNotDisturbMonth + current.totalRoomDoNotDisturb,
      }),
      {
        notCleanWithStayMonth: 0,
        notCleanWithOutMonth: 0,
        cleanedMonth: 0,
        inspectedMonth: 0,
        doNotDisturbMonth: 0,
      },
    )
  }, [monthSummary])

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', marginBottom: 11 }}>
        <img
          style={{ cursor: 'pointer' }}
          src={require('@/static/images/arrow_left_gray.svg')}
          onClick={() => {
            setDateOf(dateOf.subtract(1, 'month'))
          }}
        />
        <DateField
          customCss={overWriteDateFieldStyle}
          value={dateOf.format('YYYY-MM-DD')}
          displayFormat={t('YYYY-MM')}
          onChange={date => date && setDateOf(dayjs(date.format('YYYY-MM-DD')))}
        />
        <img
          style={{ cursor: 'pointer' }}
          src={require('@/static/images/arrow_right_gray.svg')}
          onClick={() => {
            setDateOf(dateOf.add(1, 'month'))
          }}
        />
      </div>
      <div css={listContainerStyle}>
        {!!monthSummary.length && (
          <>
            <div css={datesStyle}>
              <div css={[firstCellStyle, { paddingBlock: 0 }]} className="space"></div>
              {monthSummary.map(summary => {
                const date = dayjs(summary.date)
                const day = date.day()
                return (
                  <div key={summary.date} className="date-box" css={cellStyle}>
                    <span className="day" style={{ color: fontColorOfDay(day) }}>
                      {weekdays[day]}
                    </span>
                    <span className="date">{date.date()}</span>
                  </div>
                )
              })}
              <div className="sum" css={[cellStyle, { display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }]}>
                {t('total of')}
              </div>
            </div>

            <div css={headStyle}>
              <p css={[firstCellStyle, subTitleStyle]}>{t('summary')}</p>
              {monthSummary.map(summary => (
                <div key={`summary-${summary.date}`} css={emptyCellStyle}></div>
              ))}
              <div className="sum"></div>
            </div>
            <div>
              <div css={summaryStyle}>
                <p css={[statusTitleStyle, firstCellStyle, { color: '#F47110' }]}>{t('cleaningManager.Stay Cleaning')}</p>
                {monthSummary.map(summary => (
                  <div key={summary.date} css={cellStyle}>
                    {summary.totalRoomNotCleanWithStay}
                  </div>
                ))}
                <div className="sum" css={cellStyle}>
                  {computedTotal.notCleanWithStayMonth}
                </div>
              </div>

              <div css={summaryStyle}>
                <p css={[statusTitleStyle, firstCellStyle, { color: '#F2A40B' }]}>{t('cleaningManager.Out Cleaning')}</p>
                {monthSummary.map(summary => (
                  <div key={summary.date} css={cellStyle}>
                    {summary.totalRoomNotCleanWithOut}
                  </div>
                ))}
                <div className="sum" css={cellStyle}>
                  {computedTotal.notCleanWithOutMonth}
                </div>
              </div>

              <div css={summaryStyle}>
                <p css={[statusTitleStyle, firstCellStyle, { color: '#3E85CC' }]}>{t('cleaningManager.Clean')}</p>
                {monthSummary.map(summary => (
                  <div key={summary.date} css={cellStyle}>
                    {summary.totalRoomCleaned}
                  </div>
                ))}
                <div className="sum" css={cellStyle}>
                  {computedTotal.cleanedMonth}
                </div>
              </div>

              <div css={summaryStyle}>
                <p css={[statusTitleStyle, firstCellStyle, { color: '#7DC931' }]}>{t('cleaningManager.Inspected')}</p>
                {monthSummary.map(summary => (
                  <div key={summary.date} css={cellStyle}>
                    {summary.totalRoomInspected}
                  </div>
                ))}
                <div className="sum" css={cellStyle}>
                  {computedTotal.inspectedMonth}
                </div>
              </div>

              <div css={summaryStyle}>
                <p css={[statusTitleStyle, firstCellStyle, { color: '#676767' }]}>{t('cleaningManager.Do not Disturb')}</p>
                {monthSummary.map(summary => (
                  <div key={summary.date} css={cellStyle}>
                    {summary.totalRoomDoNotDisturb}
                  </div>
                ))}
                <div className="sum" css={cellStyle}>
                  {computedTotal.doNotDisturbMonth}
                </div>
              </div>
            </div>
          </>
        )}

        {!!staffList.length && (
          <>
            <div css={headStyle}>
              <p css={[firstCellStyle, subTitleStyle]}>{t('Person in charge')}</p>
              {monthSummary.map(summary => (
                <div key={`person-${summary.date}`} css={emptyCellStyle}></div>
              ))}
              <div className="sum"></div>
            </div>
            {staffList.map(staff => (
              <div key={staff.id} css={staffListStyle}>
                <p css={[firstCellStyle, { fontSize: 12, fontWeight: 'bold', borderBottom: '1px solid #F2F2F2' }]}>{staff.name}</p>
                {createDates.map(date => {
                  const isShift = staff.shifts.includes(date)
                  return (
                    <div
                      key={date}
                      style={{ background: isShift ? '#FEFCE0' : '#fff' }}
                      className="count"
                      css={cellStyle}
                      onClick={() => onShiftChange({ dateOf: date, staffId: staff.id })}
                    >
                      {staff.assignments[date] || 0}
                    </div>
                  )
                })}
                <div className="sum" css={cellStyle}>
                  {Object.values(staff.assignments).reduce((sum, value) => sum + value, 0)}
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  )
}

const listContainerStyle = css(mainContainerStyle, {
  padding: 0,
  '.sum': {
    flex: '0 0 33px',
    backgroundColor: '#FAFAFA',
    padding: '6px 0',
  },
})

const datesStyle = css({
  display: 'flex',
  '.date-box': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    lineHeight: 1,
  },
  '.day': {
    fontSize: 10,
  },
  '.date': {
    fontSize: 12,
  },
})

const summaryStyle = css({
  display: 'flex',
  p: {
    fontSize: 11,
    fontWeight: 'bold',
    borderLeft: 'none',
  },
})

const staffListStyle = css({
  display: 'flex',
  '.count': {
    cursor: 'pointer',
  },
})

const headStyle = css({
  display: 'flex',
  width: '100%',
})

const statusTitleStyle = css({
  fontSize: 11,
  fontWeight: 'bold',
})

const subTitleStyle = css({
  fontSize: 12,
  fontWeight: 'bold',
  background: '#FAFAFA',
})

const firstCellStyle = css({
  flex: '0 0 89px',
  padding: '9px 0px 9px 16px',
  background: '#fff',
  borderBottom: '1px solid #F2F2F2',
  borderRight: '1px solid #F2F2F2',
})

const cellStyle = css({
  flex: '0 0 2.83%',
  padding: '6px 0',
  fontSize: 12,
  fontWeight: 'bold',
  textAlign: 'center',
  lineHeight: 2,
  background: '#fff',
  borderBottom: '1px solid #F2F2F2',
  borderRight: '1px solid #F2F2F2',
})

const emptyCellStyle = css(cellStyle, {
  backgroundColor: '#FAFAFA',
  borderRight: 'none',
})

const overWriteDateFieldStyle = css({
  'div.SingleDatePicker': {
    '.SingleDatePickerInput': {
      width: 74,
      border: 'none',
      backgroundColor: 'transparent',
      'div.SingleDatePicker_picker': {
        zIndex: 100,
      },
      '.SingleDatePickerInput_calendarIcon': { display: 'none' },
      'div.DateInput': {
        'input.DateInput_input': {
          padding: '5px 0',
          fontSize: 12,
          lineHeight: '24px',
          fontWeight: 'bold',
          color: 'rgba(0, 0, 0, 0.65)',
          letterSpacing: '0.8px',
        },
      },
    },
  },
})
